'use client'

import Cookies from 'universal-cookie'

import { isBrowser } from '../../../../lib/utils'
import { cookieKeys } from '../../../_config/Cookies.config'

type RequestContext = {
  __TypeInfo: 'request'
  pageUrl: string
  pageTitle: string
  timezone: string
  userAgent: string
}

type UserContext = {
  __TypeInfo: 'user'
  emailAddress: string
}

type ThirdPartyContext = {
  __TypeInfo: 'thirdparty'
  heapSessionId: string
}

type MarketingContext = {
  __TypeInfo: 'marketing'
  source?: string
  medium?: string
  campaign?: string
  ttCampaignId?: string
  initialPage?: string
  initialReferrer?: string
}

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue }

type EventToTrack = {
  event: string
  attributes: Record<string, JSONValue>
  user?: Omit<UserContext, '__TypeInfo'>
}

export class SignalClient {
  track({ event, attributes, user }: EventToTrack) {
    if (process.env.NEXT_PUBLIC_USER_SIGNALS_ENABLED !== 'true') return

    const data = {
      event: event,
      attributes: attributes,
      context: this.createContext({ user }),
      createdAt: new Date().toISOString(),
    }

    const url = process.env.NEXT_PUBLIC_CLIENT_API_BASE_URL + '/api/user-signal/v1/signals'

    const headers = new Headers({
      'content-type': 'application/json',
    })

    const authorization = new Cookies().get('figs-auth-token')
    if (authorization) {
      headers.append('Authorization', authorization)
    }

    fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
    }).catch(error => console.error(error))
  }

  private createContext({ user }: { user?: Omit<UserContext, '__TypeInfo'> }) {
    const context = []

    if (user) {
      context.push(this.buildUserContext({ user }))
    }

    if (isBrowser()) {
      context.push(this.buildRequestContext())

      const marketingContext = this.getMarketingContext()
      if (marketingContext) {
        context.push(marketingContext)
      }

      const thirdpartyContext = this.buildThirdPartyContext()
      if (thirdpartyContext) {
        context.push(thirdpartyContext)
      }
    }

    return context
  }

  private buildRequestContext() {
    const requestContext: RequestContext = {
      __TypeInfo: 'request',
      pageTitle: document.title,
      pageUrl: document.URL,
      timezone: Intl?.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: window.navigator.userAgent,
    }
    return requestContext
  }

  private getMarketingContext(): MarketingContext | null {
    const sessionSourceData = new Cookies().get(cookieKeys.sessionSource.key)
    if (sessionSourceData) {
      try {
        return {
          __TypeInfo: 'marketing',
          ...sessionSourceData,
        }
      } catch {}
    }

    // This fallback should never occur since we set session source in CloudFront functions
    return null
  }

  private buildUserContext({ user }: { user: Omit<UserContext, '__TypeInfo'> }) {
    const userContext: UserContext = {
      __TypeInfo: 'user',
      emailAddress: user.emailAddress,
    }
    return userContext
  }

  private buildThirdPartyContext() {
    let heapSessionId = null
    if (globalThis.heap?.getSessionId) {
      heapSessionId = globalThis.heap?.getSessionId()
    } else {
      const heapCookie = this.lookupHeapCookie()

      if (heapCookie) {
        heapSessionId = heapCookie.sessionId
      }
    }

    if (heapSessionId !== null) {
      const thirdpartyContext: ThirdPartyContext = {
        __TypeInfo: 'thirdparty',
        heapSessionId: heapSessionId,
      }
      return thirdpartyContext
    }
    return null
  }

  private lookupHeapCookie() {
    const heapCookieId = '_hp2_id.' + process.env.NEXT_PUBLIC_HEAP_APP_ID

    return new Cookies().get(heapCookieId)
  }
}
